/**
 * MembershipDetails.js — Manage an existing club membership (kind
 * "membership-details").
 *
 * Common Commerce7 club feature — parity with old CHECKOUT.membership.
 * showMembershipDetails: club/subscription summary, shipping address,
 * payment method, wine list, and edit actions per section (old
 * data-change-delivery / data-change-payment / data-change-frequency /
 * data-change-status).
 *
 * message.data: { membership: <Commerce7 club-membership> }
 *
 * EVENTS OUT: ws-membership-edit {type, membership}
 */

import { getFeature } from "../../../core/configAccessors.js";
import { emit } from "../../../core/state.js";

const EDIT_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>`;

function groupHead(titleText, onEdit) {
  const head = document.createElement("div");
  head.className = "ws-order-head";
  const title = document.createElement("p");
  title.className = "ws-order-group-title";
  title.textContent = titleText;
  head.appendChild(title);
  if (onEdit) {
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "ws-order-edit";
    edit.innerHTML = EDIT_ICON;
    edit.addEventListener("click", onEdit);
    head.appendChild(edit);
  }
  return head;
}

function textLine(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div;
}

// Club-package for-web items carry defaultQuantity (not quantity/extPrice like
// a cart item) — compute the line total the same way the old code did.
function wineLine(p) {
  const line = document.createElement("div");
  line.className = "ws-order-item";

  if (p.image) {
    const imgWrap = document.createElement("div");
    imgWrap.className = "ws-order-item-img-wrap";
    const img = document.createElement("img");
    img.className = "ws-order-item-img";
    img.src = p.image;
    img.alt = p.productTitle || "Wine";
    img.loading = "lazy";
    imgWrap.appendChild(img);
    line.appendChild(imgWrap);
  }

  const body = document.createElement("div");
  body.className = "ws-order-item-body";

  const t = document.createElement("strong");
  t.className = "ws-order-item-title";
  t.textContent = p.productTitle || "";
  body.appendChild(t);

  const desc = document.createElement("p");
  desc.className = "ws-order-item-desc";
  desc.textContent = [p.productVariantTitle, `${p.price || ""} per bottle`].filter(Boolean).join(" | ");
  body.appendChild(desc);

  const footer = document.createElement("div");
  footer.className = "ws-order-item-footer";
  const qty = document.createElement("p");
  qty.className = "ws-order-item-qty";
  qty.textContent = `${p.price || ""} X ${p.defaultQuantity}`;
  const price = document.createElement("p");
  price.className = "ws-order-item-price";
  const unitPrice = parseFloat(String(p.price || "0").replace(/[^0-9.]/g, "")) || 0;
  price.textContent = `$${(unitPrice * p.defaultQuantity).toFixed(2)}`;
  footer.appendChild(qty);
  footer.appendChild(price);
  body.appendChild(footer);

  line.appendChild(body);
  return line;
}

export default class MembershipDetails {
  render(message) {
    const membership = message?.data?.membership || {};
    const club = membership.club || {};
    const shipTo = membership.shipTo || {};
    const pickupBy = membership.pickupBy || {};
    const isPickup = membership.orderDeliveryMethod === "Pickup";
    const card = membership.customerCreditCard || membership.creditCard || {};
    const frequency = membership.subscription?.frequency;
    let wines = [];

    if(getFeature("commerce7")){
      wines = membership?.customer?.club?.subscription?.items || [];
    }

    const wrap = document.createElement("div");
    wrap.className = "ws-order-review";

    const title = document.createElement("p");
    title.className = "ws-order-title";
    title.textContent = club.title || "Your Membership";
    wrap.appendChild(title);

    const status = document.createElement("p");
    status.className = "ws-order-item-desc";
    status.innerHTML = `Status: <span style="background:#bafae1;padding:2px 8px;border-radius:12px;">${membership.status}</span>`;
    wrap.appendChild(status);

    const divider = document.createElement("hr");
    divider.className = "ws-order-divider";
    wrap.appendChild(divider);

    // Frequency — only shown when the membership actually has one; "Not set"
    // wasn't a useful row and its edit pencil had nothing meaningful to edit.
    if (frequency) {
      const freqGroup = document.createElement("div");
      freqGroup.className = "ws-order-summary-group";
      freqGroup.appendChild(
        groupHead("Frequency", () => emit("ws-membership-edit", { type: "frequency", membership })),
      );
      freqGroup.appendChild(textLine(`Every ${frequency} month${Number(frequency) === 1 ? "" : "s"}`));
      wrap.appendChild(freqGroup);
    }

   // Delivery
    const deliveryGroup = document.createElement("div");
    deliveryGroup.className = "ws-order-summary-group";
    deliveryGroup.appendChild(
      groupHead(isPickup ? "Pickup by" : "Ship to", () =>
        emit("ws-membership-edit", { type: "delivery", membership }),
      ),
    );
    const deliveryBody = document.createElement("div");
    deliveryBody.className = "ws-order-details";
 
    // Old CHECKOUT.membership template: Ship shows the recipient's name;
    // Pickup shows the pickup location's title (e.g. "RedConcierge") — same
    // orderDeliveryDetails object, just a different field per method.
    const deliveryDetails = isPickup ? pickupBy : shipTo;
 
    const nameLine = isPickup
      ? deliveryDetails.title
      : [deliveryDetails.firstName, deliveryDetails.lastName].filter(Boolean).join(" ");
    if (nameLine) deliveryBody.appendChild(textLine(nameLine));
 
    if (deliveryDetails.company) deliveryBody.appendChild(textLine(deliveryDetails.company));
 
    const addrLine = [deliveryDetails.address, deliveryDetails.address2].filter(Boolean).join(" ");
    if (addrLine) deliveryBody.appendChild(textLine(addrLine));
 
    // stateCode, city on one line; countryCode-zipCode on the next — matches
    // the pickup-location card format (old CheckoutForm.js _loadPickupLocations).
    const stateCityLine = [deliveryDetails.stateCode, deliveryDetails.city].filter(Boolean).join(", ");
    if (stateCityLine) deliveryBody.appendChild(textLine(stateCityLine));
 
    const countryZipLine = [deliveryDetails.countryCode, deliveryDetails.zipCode]
      .filter(Boolean)
      .join("-");
    if (countryZipLine) deliveryBody.appendChild(textLine(countryZipLine));
 
    if (deliveryDetails.phone) deliveryBody.appendChild(textLine(`Phone: ${deliveryDetails.phone}`));
 
    deliveryGroup.appendChild(deliveryBody);
    wrap.appendChild(deliveryGroup);


    // Payment
    const paymentGroup = document.createElement("div");
    paymentGroup.className = "ws-order-summary-group";
    paymentGroup.appendChild(
      groupHead("Payment", () => emit("ws-membership-edit", { type: "payment", membership })),
    );
    if (card.maskedCardNumber) {
      // Same two-line treatment as ClubCheckoutForm.js's saved-card rows:
      // bold brand on its own line, masked number + expiry below it.
      const brand = document.createElement("div");
      brand.style.fontWeight = "600";
      brand.textContent = (card.cardBrand || "Card").toUpperCase();
      paymentGroup.appendChild(brand);
 
      const details = document.createElement("div");
      details.style.fontSize = "12px";
      details.textContent = `${card.maskedCardNumber} | Expiry: ${card.expiryMo}/${card.expiryYr}`;
      paymentGroup.appendChild(details);
    } else {
      paymentGroup.appendChild(textLine("No card on file"));
    }
    wrap.appendChild(paymentGroup);

    // Wines — the club's current active-package selection (old parity:
    // CHECKOUT.membership.showMembershipDetails' "Wines" section). Only items
    // with a defaultQuantity are shown, same filter the old code used.
    const wineItems = (membership.membership_wines?.items || []).filter((p) => p.defaultQuantity);
    if (wineItems.length) {
      const wineGroup = document.createElement("div");
      wineGroup.className = "ws-order-summary-group";
      wineGroup.appendChild(groupHead("Wines"));
      wineItems.forEach((p) => wineGroup.appendChild(wineLine(p)));
      wrap.appendChild(wineGroup);
    }


    // Cancel
    const footer = document.createElement("div");
    footer.className = "ws-order-footer";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "ws-cart-action-btn";
    cancel.textContent = "Cancel membership";
    cancel.addEventListener("click", () => emit("ws-membership-edit", { type: "cancel", membership }));
    footer.appendChild(cancel);
    wrap.appendChild(footer);

    return wrap;
  }
}